import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useModules } from '../lib/modules';
import { useCurrentSession } from '../stores/session';
import { useUi } from '../stores/ui';
import { goShortcutsFor } from './nav-config';

// The global keyboard layer (06-qol §2).
//
// Mounted once by the shell. Everything here checks that the user is not typing
// first: a `?` inside a rejection reason must be a question mark, not a shortcut,
// and that mistake is the fastest way to make people distrust the keyboard.

/** How long the `g` prefix stays armed before it lapses back to nothing. */
const SEQUENCE_WINDOW_MS = 1_000;

function isTypingIn(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function useGlobalShortcuts(): void {
  const navigate = useNavigate();
  const session = useCurrentSession();
  const modules = useModules();
  const openPalette = useUi((state) => state.openPalette);
  const closePalette = useUi((state) => state.closePalette);
  const toggleShortcutSheet = useUi((state) => state.toggleShortcutSheet);

  /** Set when `g` is pressed, cleared on the next key or when the window lapses. */
  const pendingSequence = useRef<number | null>(null);

  useEffect(() => {
    const shortcuts = goShortcutsFor(session, modules);

    const onKeyDown = (event: KeyboardEvent): void => {
      const isModifier = event.metaKey || event.ctrlKey;

      // Ctrl/Cmd+K works everywhere, including inside a field — it is how you
      // leave a field to go somewhere else.
      if (isModifier && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openPalette();
        return;
      }

      // 06-qol §2: Ctrl/Cmd+Enter submits the form the person is in — unless that form already
      // took the key itself, or its submit button says it is not ready.
      if (isModifier && event.key === 'Enter') {
        submitFormAround(event);
        return;
      }

      if (isTypingIn(event.target) || event.altKey || isModifier) return;

      if (event.key === '?') {
        event.preventDefault();
        toggleShortcutSheet();
        return;
      }

      // `/` focuses the primary search or filter on a list screen. Falls back to
      // the palette when the screen has no search of its own, so the key always
      // does something rather than silently nothing.
      if (event.key === '/') {
        event.preventDefault();
        const search = document.querySelector<HTMLInputElement>('[data-primary-search]');
        if (search) search.focus();
        else openPalette();
        return;
      }

      if (event.key === 'g') {
        if (pendingSequence.current !== null) window.clearTimeout(pendingSequence.current);
        pendingSequence.current = window.setTimeout(() => {
          pendingSequence.current = null;
        }, SEQUENCE_WINDOW_MS);
        return;
      }

      if (pendingSequence.current === null) return;

      // `g` is armed, so this key is the second half of a navigation sequence.
      window.clearTimeout(pendingSequence.current);
      pendingSequence.current = null;

      const destination = shortcuts.get(event.key.toLowerCase());
      if (!destination) return;
      event.preventDefault();
      closePalette();
      void navigate({ to: destination.to });
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (pendingSequence.current !== null) window.clearTimeout(pendingSequence.current);
    };
  }, [session, modules, navigate, openPalette, closePalette, toggleShortcutSheet]);
}

function submitFormAround(event: KeyboardEvent): void {
  if (event.defaultPrevented || !(event.target instanceof HTMLElement)) return;
  const form = event.target.closest('form');
  const submit = form?.querySelector<HTMLButtonElement>('button[type="submit"]:not([disabled])');
  if (!form || !submit) return;
  event.preventDefault();
  form.requestSubmit(submit);
}
